import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { useDemBlanketOrdersRealtime } from '../../hooks/useDemBlanketOrdersRealtime';
import { listBlanketOrders } from '../../lib/demBlanketOrders';
import type { DemBlanketOrder, DemBlanketOrderStatus } from '../../types/dem';

type StatusFilter = DemBlanketOrderStatus | 'All';

function formatQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n)
    ? String(n)
    : n.toLocaleString('en-SG', { maximumFractionDigits: 2 });
}

export function DemBlanketOrdersListPage() {
  const [status, setStatus] = useState<StatusFilter>('Open');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState<DemBlanketOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listBlanketOrders({ status, search: debouncedSearch });
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load blanket orders.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  useDemBlanketOrdersRealtime({
    onChange: () => {
      void load();
    },
  });

  return (
    <div className="delivery-page delivery-page--floating dem-list-scroll">
      <div className="dem-floating-page-actions dem-floating-page-actions--end">
        <div className="dem-floating-page-actions__btns">
          <button type="button" className="btn-secondary btn-sm" onClick={() => void load()}>
            Refresh
          </button>
          <Link to="/dem/blanket-orders/new" className="btn-primary">
            New blanket
          </Link>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search PO or customer…"
        leading={
          <div className="dem-blanket-list__toolbar-actions">
            <select
              className="field-input delivery-page__type-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              aria-label="Status"
            >
              <option value="Open">Open</option>
              <option value="Completed">Completed</option>
              <option value="Closed">Closed</option>
              <option value="All">All</option>
            </select>
          </div>
        }
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? (
        <div className="empty-state">Loading blanket orders…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          No blanket orders found.
          <Link to="/dem/blanket-orders/new" className="btn-primary btn-sm dem-blanket-list__empty-cta">
            Create one
          </Link>
        </div>
      ) : (
        <div className="card dem-blanket-list">
          <div className="dem-table-scroll">
            <div className="dem-blanket-list__head" role="row">
              <span>Status</span>
              <span>Customer</span>
              <span>PO number</span>
              <span>Items</span>
            </div>
            {rows.map((row) => {
              const lines = row.lines ?? [];
              return (
                <Link
                  key={row.id}
                  to={`/dem/blanket-orders/${row.id}`}
                  className={`dem-blanket-list__row${row.outstanding_qty < 0 ? ' dem-blanket-list__row--over' : ''}`}
                >
                  <span className="dem-blanket-list__status">
                    <span
                      className={`delivery-status-pill ${
                        row.status === 'Completed'
                          ? 'delivery-status-pill--done'
                          : row.status === 'Closed'
                            ? 'delivery-status-pill--neutral'
                            : 'delivery-status-pill--active'
                      }`}
                    >
                      {row.status}
                    </span>
                  </span>

                  <span className="dem-blanket-list__customer">
                    <span className="dem-blanket-list__customer-name">
                      {row.customer_name || '—'}
                    </span>
                    {row.customer_code ? (
                      <span className="dem-blanket-list__customer-code">{row.customer_code}</span>
                    ) : null}
                  </span>

                  <span className="dem-blanket-list__po">{row.po_number}</span>

                  <span className="dem-blanket-list__items">
                    <span className="dem-blanket-list__items-head" aria-hidden>
                      <span>Item</span>
                      <span>Ordered</span>
                      <span>Delivered</span>
                      <span>Outstanding</span>
                    </span>
                    {lines.length === 0 ? (
                      <span className="dem-blanket-list__items-empty">No items</span>
                    ) : (
                      lines.map((line) => (
                        <span key={line.id} className="dem-blanket-list__item-line">
                          <span className="dem-blanket-list__item-cell">
                            <span className="dem-blanket-list__item-code">{line.item_code}</span>
                            {line.description ? (
                              <span className="dem-blanket-list__item-desc">{line.description}</span>
                            ) : null}
                          </span>
                          <span className="dem-blanket-list__item-num">{formatQty(line.ordered_qty)}</span>
                          <span className="dem-blanket-list__item-num">{formatQty(line.delivered_qty)}</span>
                          <span
                            className={`dem-blanket-list__item-num${
                              line.outstanding_qty < 0 ? ' dem-blanket-over' : ''
                            }`}
                          >
                            {formatQty(line.outstanding_qty)}
                          </span>
                        </span>
                      ))
                    )}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
